if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.formInput = this.form?.querySelector("[name=id]");
        if (this.formInput) this.formInput.disabled = false;
        this.form?.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.cartType = this.cart?.dataset.cartType || "page";

        this.submitButton = this.querySelector('[type="submit"]');
        this.body = document.querySelector("body");

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        this.handleErrorMessage = this.handleErrorMessage.bind(this);
        this.redirectAfterSubmit = this.redirectAfterSubmit.bind(this);
        this.disableLoadingInButton = this.disableLoadingInButton.bind(this);
      }

      closeComplementaryProduct() {
        const complementaryOverlayPage = document.querySelector(
          ".wt__quick-buy--page-overlay",
        );
        if (this.body.classList.contains("quick-buy-page-overlay"))
          this.body.classList.remove("quick-buy-page-overlay");
        if (
          complementaryOverlayPage.classList.contains(
            "wt__quick-buy--page-overlay--open",
          )
        )
          complementaryOverlayPage.classList.remove(
            "wt__quick-buy--page-overlay--open",
          );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // Per-product quantity limit (sum across variants of same product)
        if (window.quantityLimit) {
          const max = window.quantityLimit.perVariantMax;
          const qtyInput = this.form?.querySelector('[name="quantity"]');
          const variantIdInput = this.form?.querySelector('[name="id"]');
          const requestedQty = parseInt(qtyInput?.value, 10) || 1;

          if (variantIdInput && requestedQty > 0) {
            this._checkProductLimit(
              variantIdInput.value,
              requestedQty,
              qtyInput,
              max,
            )
              .then((result) => {
                if (!result?.allowed) {
                  // blocked, message already shown
                  return;
                }
                this._doActualSubmit({ keepError: result.keepError });
              })
              .catch(() => this._doActualSubmit());
            return;
          }
        }

        this._doActualSubmit();
      }

      async _checkProductLimit(variantId, qty, qtyInput, max) {
        try {
          let currentProductVariantIds = (
            this.form?.dataset?.productVariantIds || ""
          )
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

          if (currentProductVariantIds.length === 0) {
            currentProductVariantIds = [String(variantId)];
          }

          const index = window.CartQtyIndex
            ? await window.CartQtyIndex.getIndex()
            : null;

          let existingProductQty = 0;
          let existingCartQty = 0;
          if (index) {
            existingProductQty = window.CartQtyIndex.qtyForVariantIds(
              currentProductVariantIds,
              index,
            );
            existingCartQty = window.CartQtyIndex.totalQty(index);
          } else {
            // Fallback when cart.js is not on the page
            const res = await fetch(`${routes.cart_url}.js`);
            if (!res.ok) throw new Error("cart fetch failed");
            const cart = await res.json();
            cart.items.forEach((it) => {
              existingCartQty += it.quantity || 0;
              if (currentProductVariantIds.includes(String(it.variant_id))) {
                existingProductQty += it.quantity;
              }
            });
          }

          const cartMax = window.quantityLimit.cartMax;
          let allowed = qty;
          let limitMessage = "";

          if (existingProductQty + qty > max) {
            allowed = Math.min(allowed, Math.max(0, max - existingProductQty));
            limitMessage = window.quantityLimit.messageWithCart;
          }

          if (cartMax && existingCartQty + qty > cartMax) {
            allowed = Math.min(allowed, Math.max(0, cartMax - existingCartQty));
            limitMessage = window.quantityLimit.cartMessageWithCart;
          }

          if (allowed >= qty) return { allowed: true, keepError: false };

          if (allowed <= 0) {
            if (qtyInput) qtyInput.value = 1;
            this.handleErrorMessage(limitMessage);
            return { allowed: false, keepError: true };
          }

          // Partial room left — clamp qty and continue add so cart stays in sync
          if (qtyInput) qtyInput.value = allowed;
          this.handleErrorMessage(limitMessage);
          return { allowed: true, keepError: true };
        } catch (e) {
          // If cart lookup fails, fall through and allow submit
          console.warn("Quantity limit check skipped:", e);
          return { allowed: true, keepError: false };
        }
      }

      _doActualSubmit({ keepError = false } = {}) {
        if (!keepError) this.handleErrorMessage();
        this.cart?.setActiveElement(document.activeElement);

        const loader = this.querySelector(".loading-overlay__spinner");

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (loader) loader.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart && typeof this.cart.getSectionsToRender === "function") {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
        } else {
          formData.append("sections", []);
        }

        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              this.dispatchEvent(
                new CustomEvent("cart-drawer:refresh", {
                  detail: { response: response },
                  bubbles: true,
                  composed: true,
                }),
              );

              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.redirectAfterSubmit(response);
                  });
                },
                { once: true },
              );
              if (this.cartType === "drawer") quickAddModal.hide(true);
            } else {
              const isClosedCart = !document.body.classList.contains(
                "page-overlay-cart-on",
              );
              this.redirectAfterSubmit(response, isClosedCart);
            }
          })
          .catch((e) => {
            console.error(e);
            if (e instanceof TypeError && e.message.includes("'cart-drawer'")) {
              location.reload();
            }
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            this.submitButton.removeAttribute("aria-disabled");
            if (this.error) {
              this.disableLoadingInButton();
              return;
            }
            this.quick_add_container = document.querySelector(
              ".wt__quick-buy__container",
            );
            this.quick_add_overlay = document.querySelector(
              ".wt__quick-buy--page-overlay",
            );
            this.quick_add_product =
              this.quick_add_container.querySelector(".wt-product");
            this.loader = this.quick_add_container.querySelector(
              ".wt__quick-buy-loader",
            );

            if (!this.quick_add_container.classList.contains("hidden")) {
              // use handle and quickbuy closeCart()

              this.quick_add_product?.remove();
              this.quick_add_container.classList.remove(
                "wt__quick-buy__container--open",
              );
              this.quick_add_overlay.classList.remove(
                "wt__quick-buy--page-overlay--open",
              );
              document.body.classList.remove("quick-buy-page-overlay");
              this.loader.classList.remove("hidden");
            }

            // delay button loading
            setTimeout(() => {
              this.disableLoadingInButton();
            }, 200);
          });
      }

      disableLoadingInButton() {
        this.submitButton.classList.remove("loading");
        this.querySelector(".loading-overlay__spinner").classList.add("hidden");
      }

      redirectAfterSubmit(response, isClosedCart = true) {
        const body = document.body;
        const isCartPage = body.classList.contains("template-cart");

        if (this.cartType === "drawer" && isCartPage) {
          const overlay = document.querySelector(
            ".wt__quick-buy--page-overlay",
          );
          overlay.classList.remove("wt__quick-buy--page-overlay--open");
          body.classList.remove("quick-buy-page-overlay");
        } else if (this.cartType === "drawer") {
          this.cart.renderContents(response, isClosedCart);
          return;
        }
        window.location = window.routes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        const isLimitMessage =
          typeof errorMessage === "string" &&
          errorMessage.includes("quantity-limit-cart-link");
        this.errorMessageWrapper.classList.toggle(
          "product-form__error-message-wrapper--limit",
          Boolean(errorMessage && isLimitMessage),
        );

        if (errorMessage) {
          // Only trust theme HTML for quantity-limit messages; keep other errors as text.
          if (isLimitMessage) {
            this.errorMessage.innerHTML = errorMessage;
          } else {
            this.errorMessage.textContent = errorMessage;
          }

          // Intercept clicks on .quantity-limit-cart-link to open cart drawer
          // instead of navigating to /cart (left-click only; middle/right-click keep default).
          const cartLink = this.errorMessage.querySelector(
            ".quantity-limit-cart-link",
          );
          if (cartLink) {
            cartLink.addEventListener(
              "click",
              (event) => {
                const hasDrawer = !!document.querySelector("cart-drawer");
                if (
                  hasDrawer &&
                  event.button === 0 &&
                  !event.metaKey &&
                  !event.ctrlKey &&
                  !event.shiftKey
                ) {
                  event.preventDefault();
                  document.dispatchEvent(
                    new CustomEvent("cart:open-drawer", { bubbles: true }),
                  );
                }
              },
              { once: true },
            );
          }
        }
      }
    },
  );
}
